import React, { useState, useEffect, useMemo, useRef } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { materialManager } from "./materialManager";
import { getTranslations } from "./components/translations";

// Helper function to get proper image URL
const getImageUrl = (thumbnail) => {
  // If it's a hex color, return as is
  if (thumbnail && thumbnail.startsWith("#")) {
    return thumbnail;
  }

  // If it already has a full URL, return as is
  if (
    thumbnail &&
    (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
  ) {
    return thumbnail;
  }

  // If it's a relative path, use it directly (works on any domain)
  if (thumbnail) {
    // Remove any leading ./ from the path and ensure it starts with /
    const cleanPath = thumbnail.replace(/^\.\//, "");
    return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  }

  // Default case
  return "#";
};

// NEW: Simple and clean tooltip component
const MaterialTooltip = ({ children, hoverText }) => {
  if (!hoverText) return children;

  return (
    <div className="ay-relative ay-group/tooltip">
      {children}
      {/* Simple tooltip positioned above the element */}
      <div className="ay-absolute ay-bottom-full ay-left-1/2 ay-transform ay--translate-x-1/2 ay--translate-y-1 ay-px-2 ay-py-1 ay-text-xs ay-bg-black ay-text-white ay-rounded ay-whitespace-nowrap ay-opacity-0 ay-pointer-events-none ay-transition-opacity ay-duration-200 ay-z-10 group-hover/tooltip:ay-opacity-100">
        {hoverText}
        {/* Small arrow pointing down */}
        <div className="ay-absolute ay-top-full ay-left-1/2 ay-transform ay--translate-x-1/2 ay-w-0 ay-h-0 ay-border-l-2 ay-border-r-2 ay-border-t-2 ay-border-transparent ay-border-t-black"></div>
      </div>
    </div>
  );
};

export const Accordion = ({
  title,
  variations,
  selected,
  onChange,
  standardMaterials = [],
  optionalMaterials = [],
  language = "it", // Default to Italian if not provided
  collectionTranslations = {}, // NUOVA PROP per traduzioni specifiche collezioni
  technicalTypeTranslations = {}, // NUOVA PROP per traduzioni tipi tecnici
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Get translations based on the language prop
  const translations = getTranslations(language);

  // Update content height when isOpen changes or content changes
  useEffect(() => {
    if (isOpen && contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    } else {
      setContentHeight(0);
    }
  }, [isOpen, variations, selected]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelect = (material) => {
    onChange(material);
  };

  // Helper function for normalization - define outside of useMemo
  const normalize = (str) =>
    String(str || "")
      .toLowerCase()
      .replace(/[\s-]+/g, "");

  // Define isStandard and isOptional functions at component level
  const isStandard = (material) => {
    // First check if the material itself is marked as standard
    if (material?.standard === true) {
      return true;
    }

    const normalizedName = normalize(material?.name || "");
    const normalizedSlug = normalize(material?.slug || "");

    return standardMaterials.some((stdMat) => {
      const normalizedStdMat = normalize(stdMat);
      return (
        normalizedName === normalizedStdMat ||
        normalizedSlug === normalizedStdMat ||
        material?.name === stdMat ||
        material?.slug === stdMat
      );
    });
  };

  const isOptional = (material) => {
    // First check if the material itself is marked as optional
    if (material?.optional === true) {
      return true;
    }

    const normalizedName = normalize(material?.name || "");
    const normalizedSlug = normalize(material?.slug || "");

    return optionalMaterials.some((optMat) => {
      const normalizedOptMat = normalize(optMat);
      return (
        normalizedName === normalizedOptMat ||
        normalizedSlug === normalizedOptMat ||
        material?.name === optMat ||
        material?.slug === optMat
      );
    });
  };

  // AGGIORNATA: Funzione per tradurre i nomi delle collezioni usando traduzioni specifiche famiglia
  const getTranslatedCollection = (collection) => {
    if (!collection)
      return translations.materialCollections?.Default || "Default";

    // Prima prova con le traduzioni specifiche della famiglia
    if (
      collectionTranslations[collection] &&
      collectionTranslations[collection][language]
    ) {
      return collectionTranslations[collection][language];
    }

    // Fallback alle traduzioni globali
    if (
      translations.materialCollections &&
      (translations.materialCollections[collection] ||
        translations.materialCollections[collection.toLowerCase()])
    ) {
      return (
        translations.materialCollections[collection] ||
        translations.materialCollections[collection.toLowerCase()]
      );
    }

    // Return the original if no translation available
    return collection;
  };

  // AGGIORNATA: Funzione per tradurre i nomi dei tipi tecnici
  const getTranslatedTitle = (originalTitle) => {
    // Prima prova con le traduzioni specifiche della famiglia (tipi tecnici)
    if (
      technicalTypeTranslations &&
      technicalTypeTranslations[originalTitle] &&
      technicalTypeTranslations[originalTitle][language]
    ) {
      return technicalTypeTranslations[originalTitle][language];
    }

    // Fallback alle traduzioni globali nei file translations
    if (
      translations.materialTypes &&
      translations.materialTypes[originalTitle.toLowerCase()]
    ) {
      return translations.materialTypes[originalTitle.toLowerCase()];
    }

    // Fallback al titolo originale
    return originalTitle;
  };

  // Material classification logic memoized - RAGGRUPPAMENTO PER COLLEZIONE
  const materialData = useMemo(() => {
    // Ottieni tutte le collezioni uniche
    const allCollections = new Set();
    variations.forEach((material) => {
      const collection = material.collection || "Default";
      allCollections.add(collection);
    });

    // Raggruppa materiali per collezione, poi per tipo
    const collectionGroups = {};

    Array.from(allCollections)
      .sort()
      .forEach((collection) => {
        const materialsInCollection = variations.filter(
          (material) => (material.collection || "Default") === collection
        );

        const standardMaterials = materialsInCollection.filter((m) =>
          isStandard(m)
        );

        const optionalMaterials = materialsInCollection.filter(
          (m) => !isStandard(m)
        );

        // USA LA NUOVA FUNZIONE DI ORDINAMENTO del materialManager
        const orderedStandardMaterials = materialManager.getOrderedMaterials(
          title, // technicalName
          standardMaterials,
          true // isStandardType = true
        );

        const orderedOptionalMaterials = materialManager.getOrderedMaterials(
          title, // technicalName
          optionalMaterials,
          false // isStandardType = false
        );

        // Solo aggiungi la collezione se ha materiali
        if (materialsInCollection.length > 0) {
          collectionGroups[collection] = {
            standard: orderedStandardMaterials,
            optional: orderedOptionalMaterials,
          };
        }
      });

    return collectionGroups;
  }, [variations, standardMaterials, optionalMaterials, isStandard, title]);

  // Determine if selected material is optional
  const isSelectedOptional = selected ? !isStandard(selected) : false;

  return (
    <div className="ay-w-full ay-py-2 ">
      <div
        className="ay-group ay-gap-x-4 ay-flex ay-justify-between ay-cursor-pointer "
        onClick={handleToggle}
      >
        {/* Header section with aligned title and icon */}
        <div className="ay-flex-grow">
          <div className="ay-flex ay-items-center ay-justify-between">
            <p className="ay-capitalize">{getTranslatedTitle(title)}</p>
            <div className="ay-flex ay-items-center ay-justify-center ay-w-6 ay-h-6">
              <span
                className={`ay-transition-transform  ay-duration-300 ay-ease-in-out ${
                  isOpen ? "ay-rotate-45" : "ay-rotate-0"
                }`}
              >
                <AiOutlinePlus />
              </span>
            </div>
          </div>

          {/* COMMENTED CODE - KEEPING FOR FUTURE USE */}
          {/*   {selected && (
            <div className="ay-flex ay-items-center ay-gap-2 ay-mt-1">
              <div className="ay-w-8 ay-h-8 ay-overflow-hidden ay-border ay-rounded-full ay-border-black/80">
                {selected.thumbnail && selected.thumbnail.startsWith("#") ? (
                  <div
                    className="ay-w-full ay-h-full"
                    style={{ backgroundColor: selected.thumbnail }}
                  />
                ) : (
                  <img
                    src={getImageUrl(selected.thumbnail)}
                    alt={selected.name}
                    className="ay-w-full ay-h-full ay-rotate-90 ay-rounded-full ay-object-cover"
                    onError={(e) => {
                      e.target.onerror = null
                      e.target.src =
                        'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23cccccc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23ffffff">No Image</text></svg>'
                    }}
                  />
                )}
              </div>
              <p className="ay-font-medium  ay-text-black/40 ay-truncate">
                {selected.displayName || selected.name}
                {isStandard(selected)
                  ? ` (${translations.standard})`
                  : isOptional(selected)
                  ? ` (${translations.optional})`
                  : ""}
              </p>
            </div>
          )}
          {!selected && (
            <p className="ay-font-medium  ay-text-black/40 ay-mt-1 ay-truncate">
              {translations.select} {getTranslatedTitle(title).toLowerCase()}
            </p>
          )} */}
        </div>
      </div>

      {/* Accordion Content with animation */}
      <div
        ref={contentRef}
        className="ay-overflow-hidden ay-transition-all ay-duration-300 ay-ease-in-out"
        style={{ maxHeight: `${contentHeight}px`, opacity: isOpen ? 1 : 0 }}
      >
        <div
          className="ay-mt-4 ay-transform ay-transition-transform ay-duration-300 ay-ease-in-out"
          style={{ transform: isOpen ? "translateY(0)" : "translateY(-10px)" }}
        >
          {/* NUOVO RAGGRUPPAMENTO: Per ogni collezione */}
          {Object.entries(materialData).map(([collection, materials]) => (
            <div key={collection} className="ay-mb-6 ay-ml-1 ay-mr-1">
              {/* Nome Collezione */}
              <p className="ay-font-medium ay-text-black/60 ay-uppercase ay-mb-4 ay-border-b ay-border-gray-200 ay-pb-2">
                {getTranslatedCollection(collection)}
              </p>

              {/* Finiture Standard per questa collezione */}
              {materials.standard.length > 0 && (
                <div className="ay-mb-4">
                  <p className="ay-font-normal ay-text-black/80 ay-mb-2">
                    {translations.standardFinishes}
                  </p>
                  {/* FIXED: Better gap management for 8 columns */}
                  <div className="ay-gap-2 ay-grid ay-grid-cols-5 2xl:ay-grid-cols-8 ay-mb-4 ay-px-1">
                    {materials.standard.map((material) => (
                      <div
                        key={material.id || material.slug || material.name}
                        className="ay-cursor-pointer ay-text-center ay-group ay-flex ay-flex-col ay-items-center"
                        onClick={() => handleSelect(material)}
                        tabIndex="0"
                      >
                        {/* NEW: Wrapped thumbnail with MaterialTooltip */}
                        {/*   <MaterialTooltip hoverText={material.hover}> */}
                        <div
                          className={`ay-rounded-full ay-w-12 2xl:ay-w-14 ay-h-12 2xl:ay-h-14 ay-mb-3 ay-border ay-shadow-lg ay-transition-transform  ay-aspect-square ay-overflow-hidden focus-visible:ay-ring-2 focus-visible:ay-ring-black ${
                            selected?.slug === material.slug ||
                            selected?.name === material.name
                              ? "ay-border-1 ay-rounded-full ay-border-black"
                              : "ay-rounded-full "
                          }`}
                          tabIndex="-1"
                        >
                          {material.thumbnail &&
                          material.thumbnail.startsWith("#") ? (
                            <div
                              className="ay-w-full ay-h-full"
                              style={{ backgroundColor: material.thumbnail }}
                            />
                          ) : (
                            <img
                              src={getImageUrl(material.thumbnail)}
                              alt={material.name}
                              className="ay-w-full ay-h-full ay-rotate-90 ay-rounded-full ay-object-cover ay-scale-125 group-hover:ay-scale-250 ay-transition-transform ay-duration-500 ay-ease-in-out"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23cccccc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23ffffff">No Image</text></svg>';
                              }}
                            />
                          )}
                        </div>
                        {/*  </MaterialTooltip> */}
                        <span
                          className={`ay-block ay-font-light ay-text-[12px] 3xl:ay-text-[12px] ay-text-center ay-max-w-full ay-break-words ${
                            selected?.slug === material.slug ||
                            selected?.name === material.name
                              ? "ay-font-bold"
                              : ""
                          }`}
                        >
                          {material.displayName || material.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Finiture Opzionali per questa collezione */}
              {materials.optional.length > 0 && (
                <div className="ay-mb-4">
                  <p className="ay-font-normal ay-text-black/80 ay-mb-2">
                    {translations.optionalFinishes}
                  </p>
                  {/* FIXED: Better gap management for 8 columns */}
                  <div className="ay-gap-2 ay-grid ay-grid-cols-5 2xl:ay-grid-cols-8 ay-px-1">
                    {materials.optional.map((material) => (
                      <div
                        key={material.id || material.slug || material.name}
                        className="ay-cursor-pointer ay-text-center ay-group ay-flex ay-flex-col ay-items-center"
                        onClick={() => handleSelect(material)}
                        tabIndex="0"
                      >
                        {/* NEW: Wrapped thumbnail with MaterialTooltip */}
                        {/*    <MaterialTooltip hoverText={material.hover}> */}
                        <div
                          className={`ay-rounded-full ay-w-12 2xl:ay-w-14 ay-h-12 2xl:ay-h-14 ay-mb-3 ay-border ay-shadow-lg ay-transition-transform  ay-aspect-square ay-overflow-hidden focus-visible:ay-ring-2 focus-visible:ay-ring-black ${
                            selected?.slug === material.slug ||
                            selected?.name === material.name
                              ? "ay-1 ay-rounded-full ay-border-black"
                              : "ay-rounded-full "
                          }`}
                          tabIndex="-1"
                        >
                          {material.thumbnail &&
                          material.thumbnail.startsWith("#") ? (
                            <div
                              className="ay-w-full ay-h-full"
                              style={{ backgroundColor: material.thumbnail }}
                            />
                          ) : (
                            <img
                              src={getImageUrl(material.thumbnail)}
                              alt={material.name}
                              className="ay-w-full ay-h-full ay-rotate-90 ay-rounded-full ay-object-cover ay-scale-125 group-hover:ay-scale-250 ay-transition-transform ay-duration-500 ay-ease-in-out"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23cccccc"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="%23ffffff">No Image</text></svg>';
                              }}
                            />
                          )}
                        </div>
                        {/*   </MaterialTooltip> */}
                        <span
                          className={`ay-block ay-font-light ay-text-[12px] 3xl:ay-text-[12px] ay-text-center ay-max-w-full ay-break-words ${
                            selected?.slug === material.slug ||
                            selected?.name === material.name
                              ? "ay-font-bold"
                              : ""
                          }`}
                        >
                          {material.displayName || material.name}{" "}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
